import { decamelizeKeys } from "humps";
import { submitOauthSessionBridge, takeStashedOauthReturnTo } from "PFApp/helpers/oauth_session_bridge";
import { useAuthStoreActions } from "PFApp/stores/auth_store";
import api from "PFCore/api";
import { lockSimple } from "PFCore/helpers/lock_simple";
import { initializeMsTeams } from "PFCore/helpers/ms_teams";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useCurrentProfileSet } from "PFCore/hooks/queries/profile";
import { fetchCurrentProfile } from "PFCore/services/current_profile";
import { UiRoute } from "PFCore/utilities/routes";
import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { useDeeplink } from "../use_deeplink";
import { SignOutOptions, useSignOut } from "./use_sign_out";

export type Session = {
  refreshToken: () => Promise<any>;
  expireSession: (options?: SignOutOptions) => void;
  getAccessToken: () => string | null;
  setup: (options?: { [key: string]: unknown }) => unknown;
  signOut: (options?: SignOutOptions) => Promise<unknown>;
  signIn: (username: string, password: string, options: { onlySuccess?: boolean }) => Promise<any>;
};

export const useSession = (): Session => {
  const { refetch: refetchCurrentAccount } = useCurrentAccount();

  const setCurrentProfile = useCurrentProfileSet();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { saveDeeplink } = useDeeplink();
  const { getAccessToken, getRefreshToken, setTokens } = useAuthStoreActions();
  const { signOut } = useSignOut();

  const expireSession = useCallback(
    ({ error, skipRevoke }: SignOutOptions = {}) => {
      if (location.pathname !== "/sign_out") {
        saveDeeplink();
      }
      window.storage.removeItem("onboarding__show_job_title");
      const errorFinal = error || "sign_in.session_expired";
      signOut({ error: errorFinal, skipRevoke });
    },
    [location.pathname, saveDeeplink, signOut]
  );

  const refreshToken = () =>
    lockSimple("refresh_token", () => {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        expireSession();
        return Promise.reject();
      }

      return api({
        url: "oauth/token",
        method: "post",
        skipVersioning: true,
        data: {
          grantType: "refresh_token",
          refreshToken,
          clientId: window.PF.config.client_id
        }
      })
        .then((resp) => {
          setTokens(resp.accessToken, resp.refreshToken);
        })
        .catch((resp) => {
          // Couldn't refresh the token so we sign out
          if (resp.response.status === 400) {
            expireSession({ skipRevoke: true });
          } else {
            signOut();
          }

          return Promise.reject(resp);
        });
    });

  const signIn = (username: string, password: string, options: { onlySuccess?: boolean } = {}) =>
    api({
      url: "oauth/token",
      method: "post",
      skipVersioning: true,
      data: {
        grantType: "password",
        clientId: window.PF.config.client_id,
        username,
        password
      }
    }).then(
      (resp) => {
        setTokens(resp.accessToken, resp.refreshToken);
        if (typeof sessionStorage !== "undefined" && sessionStorage !== null) {
          sessionStorage.removeItem("signedOut");
        }

        return fetchCurrentProfile().then(
          (profile) => {
            setCurrentProfile(profile);
            refetchCurrentAccount();
            return Promise.resolve(resp);
          },
          (resp) => {
            if (!options.onlySuccess) {
              signOut();
            }
            return Promise.reject(resp);
          }
        );
      },
      (resp) => {
        if (!options.onlySuccess) {
          signOut();
        }
        return Promise.reject(resp);
      }
    );

  const setup = async () => {
    const code = searchParams.get("code");
    const resetPasswordToken = searchParams.get("reset_password_token");
    const isMsTeams = searchParams.get("msTeams");
    const account = searchParams.get("account");
    if (resetPasswordToken) {
      await signOut(
        location.pathname === "/password_edit"
          ? { redirectUri: `${location.pathname}${location.search}` }
          : {}
      );
    } else if (code) {
      try {
        const response = await api({
          url: "oauth/token",
          method: "post",
          skipVersioning: true,
          data: {
            grantType: "authorization_code",
            code,
            clientId: window.PF.config.client_id,
            redirectUri: UiRoute("/")
          }
        });
        if (isMsTeams) {
          const oauthRedirectMethod = searchParams.get("oauthRedirectMethod");
          const authId = searchParams.get("authId");
          response.account = account;
          if (oauthRedirectMethod === "deeplink") {
            const { accessToken, refreshToken } = response;
            const result = `${accessToken}//${refreshToken}//${account}`;
            window.location.href = `msteams://teams.microsoft.com/l/auth-callback?authId=${authId}&result=${result}`;
            return navigate("/ms_teams/end");
          } else {
            const microsoftTeams = (await initializeMsTeams({ force: true })) as any;
            return microsoftTeams.authentication.notifySuccess(decamelizeKeys(response));
          }
          // teams tab (ms_teams_sign_in.jsx), or bot should handle tokens from here
        }

        const oauthReturnTo = takeStashedOauthReturnTo();
        if (oauthReturnTo) {
          setTokens(response.accessToken, response.refreshToken);
          submitOauthSessionBridge(response.accessToken, oauthReturnTo);
          return response.accessToken;
        }

        // Clear code param from url so we don't perform authentication twice
        navigate("/");
        setTokens(response.accessToken, response.refreshToken);
        return response.accessToken;
      } catch {
        await signOut();
      }
    }
  };

  return {
    getAccessToken,
    signIn,
    signOut,
    refreshToken: refreshToken as () => Promise<any>,
    expireSession,
    setup
  };
};
